/**
 * Locale utilities.
 *
 * Frac is a bilingual site with English at `/*` and Simplified Chinese at
 * `/zh/*`. Locale is a URL-derived value — the URL is the single source of
 * truth. localStorage / navigator.language are consulted only on the FIRST
 * page load to seed a redirect.
 */

export const LOCALES = ["en", "zh"];
export const DEFAULT_LOCALE = "en";
export const LS_KEY = "frac.locale";

/** Read locale from a pathname. `/zh` and `/zh/...` → 'zh', everything else → 'en'. */
export function localeFromPath(pathname) {
  if (!pathname) return DEFAULT_LOCALE;
  if (pathname === "/zh" || pathname.startsWith("/zh/")) return "zh";
  return "en";
}

/** Strip a leading /zh prefix so we can reason about the "canonical" (English) path. */
export function stripLocalePrefix(pathname) {
  if (!pathname) return "/";
  if (pathname === "/zh") return "/";
  if (pathname.startsWith("/zh/")) return pathname.slice(3) || "/";
  return pathname;
}

/** Add /zh prefix to a canonical (EN) path. Preserves trailing content. */
export function addLocalePrefix(pathname, locale) {
  if (locale === "zh") {
    if (pathname === "/" || pathname === "") return "/zh";
    return `/zh${pathname}`;
  }
  return pathname === "" ? "/" : pathname;
}

/** Given the current URL, produce the counterpart URL for the OTHER locale. */
export function otherLocaleUrl(pathname, search = "", hash = "") {
  const current = localeFromPath(pathname);
  const canonical = stripLocalePrefix(pathname);
  const target = current === "zh" ? "en" : "zh";
  return {
    locale: target,
    path: addLocalePrefix(canonical, target) + (search || "") + (hash || ""),
  };
}

/**
 * Detect user preferred locale for first-visit redirect. Order:
 *   1. localStorage override
 *   2. navigator.languages / navigator.language starting with 'zh'
 *   3. DEFAULT_LOCALE
 */
export function detectPreferredLocale() {
  try {
    const stored = window.localStorage.getItem(LS_KEY);
    if (stored === "en" || stored === "zh") return stored;
  } catch (_) {
    // localStorage blocked; ignore.
  }
  try {
    const langs = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    for (const l of langs) {
      if (typeof l === "string" && l.toLowerCase().startsWith("zh")) return "zh";
    }
  } catch (_) {
    // navigator unavailable in SSR-ish contexts; ignore.
  }
  return DEFAULT_LOCALE;
}

/** Write user's explicit choice to localStorage. Only called on user toggle. */
export function persistLocalePreference(locale) {
  try {
    window.localStorage.setItem(LS_KEY, locale);
  } catch (_) {
    // ignore.
  }
}

/** BCP-47 language codes used in <html lang="…"> and og:locale. */
export const HTML_LANG = {
  en: "en-SG",
  zh: "zh-Hans",
};

/** og:locale */
export const OG_LOCALE = {
  en: "en_SG",
  zh: "zh_CN",
};
